import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { exportService } from '../../../services/exportService';
import { adminService } from '../../../services/adminService';
import { schoolService } from '../../../services/schoolService';

const ExportPreview = ({ category, onClose, onConfirm }) => {
  const [selectedFormat, setSelectedFormat] = useState(category?.formats?.[0]);
  const [selectedFilters, setSelectedFilters] = useState({
    status: 'all',
    city: 'all',
    ageGroup: 'all',
    gender: 'all',
    schoolNames: [], // For other exports (multi-select)
    schoolId: null, // For player_cards (single school)
    schoolName: null // For player_cards (store selected school name)
  });
  const [previewData, setPreviewData] = useState([]);
  const [recordCount, setRecordCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolSearchTerm, setSchoolSearchTerm] = useState('');

  useEffect(() => {
    loadCities();
    loadPreviewData();
  }, [selectedFilters]);

  useEffect(() => {
    // Only search if user has typed at least 2 characters
    if (schoolSearchTerm && schoolSearchTerm.trim().length >= 2) {
      const timer = setTimeout(() => {
        loadSchools(schoolSearchTerm);
      }, 300); // 300ms debounce

      return () => clearTimeout(timer);
    } else {
      // Clear schools if search term is too short or empty
      setSchools([]);
    }
  }, [schoolSearchTerm, selectedFilters.city]);

  const loadCities = async () => {
    try {
      const data = await adminService.getCitySettings();
      setCities(data || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadSchools = async (searchTerm = '') => {
    try {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setSchools([]);
        return;
      }

      // For player_cards, don't filter by city
      const cityFilter = category?.id === 'player_cards' 
        ? null 
        : (selectedFilters.city !== 'all' ? selectedFilters.city : null);

      const schoolData = await schoolService.searchSchools(
        searchTerm,
        cityFilter,
        20 // Limit to 20 results
      );
      
      setSchools(schoolData || []);
    } catch (error) {
      console.error('Error loading schools:', error);
      setSchools([]);
    }
  };

  const loadPreviewData = async () => {
    try {
      setLoading(true);
      const filters = buildFilters();
      
      // For player_cards, require school selection before loading preview
      if (category?.id === 'player_cards' && !filters.schoolId) {
        setPreviewData([]);
        setRecordCount(0);
        return;
      }
      
      // Get preview data
      const preview = await exportService.getPreviewData(category?.id, filters, 3);
      setPreviewData(preview || []);
      
      // Get record count
      const count = await exportService.getRecordCount(category?.id, filters);
      setRecordCount(count || 0);
    } catch (error) {
      console.error('Error loading preview data:', error);
      setPreviewData([]);
      setRecordCount(0);
    } finally {
      setLoading(false);
    }
  };

  const buildFilters = () => {
    const filters = {};
    
    if (selectedFilters.status !== 'all') {
      filters.status = selectedFilters.status;
    }
    
    // For player_cards, use schoolId instead of schoolNames
    if (category?.id === 'player_cards') {
      if (selectedFilters.schoolId) {
        filters.schoolId = selectedFilters.schoolId;
        filters.schoolName = selectedFilters.schoolName; // For reference
      }
    } else {
      // For other exports, keep existing logic
      if (selectedFilters.city !== 'all') {
        filters.city = selectedFilters.city;
      }
      
      if (selectedFilters.gender !== 'all') {
        filters.gender = selectedFilters.gender;
      }
      
      if (selectedFilters.schoolNames && selectedFilters.schoolNames.length > 0) {
        filters.schoolNames = selectedFilters.schoolNames;
      }
    }
    
    if (selectedFilters.ageGroup !== 'all') {
      filters.ageGroup = selectedFilters.ageGroup;
    }
    
    return filters;
  };

  const getHeaders = () => {
    if (previewData.length === 0) return [];
    return Object.keys(previewData[0]);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white rounded-none sm:rounded-lg shadow-xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{category?.title}</h2>
              <p className="text-sm text-gray-600 mt-1">Configure export parameters and preview data</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Configuration */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Format Selection - Hide for player_cards (PDF only) */}
          {category?.id !== 'player_cards' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">Export Format</label>
              <div className="flex gap-2 sm:gap-3">
                {category?.formats?.map(format => (
                  <button
                    key={format}
                    onClick={() => setSelectedFormat(format)}
                    className={`flex-1 px-3 sm:px-4 py-2 sm:py-3 rounded-lg border transition-all text-xs sm:text-sm touch-target ${
                      selectedFormat === format
                        ? 'bg-blue-50 border-blue-300 text-blue-700' :'bg-white border-gray-300 text-gray-700 hover:border-blue-200'
                    }`}
                  >
                    <span className="font-medium">{format}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {category?.id === 'player_cards' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Player Cards export generates a PDF with one page per player. Please select a school and apply filters.
              </p>
            </div>
          )}

          {/* Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">Filters</label>
            
            {category?.id === 'player_cards' ? (
              // Player Cards specific filters (Status, Age Group, Single School)
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Status</label>
                  <select
                    value={selectedFilters?.status}
                    onChange={(e) => setSelectedFilters({ ...selectedFilters, status: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="approved">Approved</option>
                    <option value="submitted">Submitted</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Age Group</label>
                  <select
                    value={selectedFilters?.ageGroup}
                    onChange={(e) => setSelectedFilters({ ...selectedFilters, ageGroup: e?.target?.value })}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm touch-target"
                  >
                    <option value="all">All Age Groups</option>
                    <option value="boysU14">Boys U14</option>
                    <option value="girlsU15">Girls U15</option>
                    <option value="boysU16">Boys U16</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">School <span className="text-red-500">*</span></label>
                  <div className="relative">
                    {selectedFilters.schoolName ? (
                      <div className="flex items-center gap-2 px-4 py-2 border border-blue-500 bg-blue-50 rounded-lg">
                        <span className="flex-1 text-sm font-medium text-blue-700">{selectedFilters.schoolName}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedFilters({ ...selectedFilters, schoolId: null, schoolName: null });
                            setSchoolSearchTerm('');
                          }}
                          className="text-blue-700 hover:text-red-600 font-bold"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={schoolSearchTerm}
                          onChange={(e) => setSchoolSearchTerm(e.target.value)}
                          placeholder="Search and select a school..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {schoolSearchTerm && schoolSearchTerm.trim().length >= 2 && schools.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {schools.map(school => (
                              <button
                                key={school.id}
                                type="button"
                                onClick={() => {
                                  setSelectedFilters({ 
                                    ...selectedFilters, 
                                    schoolId: school.id, 
                                    schoolName: school.name 
                                  });
                                  setSchoolSearchTerm('');
                                  setSchools([]);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm"
                              >
                                {school.name}
                                {school.city && (
                                  <span className="text-xs text-gray-500 ml-2">({school.city})</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Existing filters for other exports
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Status</label>
                    <select
                      value={selectedFilters?.status}
                      onChange={(e) => setSelectedFilters({ ...selectedFilters, status: e?.target?.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Status</option>
                      <option value="approved">Approved</option>
                      <option value="submitted">Submitted</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">City</label>
                    <select
                      value={selectedFilters?.city}
                      onChange={(e) => {
                        setSelectedFilters({ ...selectedFilters, city: e?.target?.value, schoolNames: [] });
                        setSchoolSearchTerm('');
                      }}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm touch-target"
                    >
                      <option value="all">All Cities</option>
                      {cities.map(city => (
                        <option key={city.city} value={city.city}>
                          {city.city.charAt(0).toUpperCase() + city.city.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Gender</label>
                    <select
                      value={selectedFilters?.gender}
                      onChange={(e) => setSelectedFilters({ ...selectedFilters, gender: e?.target?.value })}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm touch-target"
                    >
                      <option value="all">All Genders</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Age Group</label>
                    <select
                      value={selectedFilters?.ageGroup}
                      onChange={(e) => setSelectedFilters({ ...selectedFilters, ageGroup: e?.target?.value })}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm touch-target"
                    >
                      <option value="all">All Age Groups</option>
                      <option value="boysU14">Boys U14</option>
                      <option value="girlsU15">Girls U15</option>
                      <option value="boysU16">Boys U16</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-xs text-gray-600 mb-1">
                    School {selectedFilters.schoolNames?.length > 0 && (
                      <span className="text-blue-600">({selectedFilters.schoolNames.length} selected)</span>
                    )}
                  </label>
              
              {/* Selected Schools Display */}
              {selectedFilters.schoolNames && selectedFilters.schoolNames.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {selectedFilters.schoolNames.map((schoolName, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium"
                    >
                      {schoolName}
                      <button
                        type="button"
                        onClick={() => {
                          const newSelected = selectedFilters.schoolNames.filter(name => name !== schoolName);
                          setSelectedFilters({ ...selectedFilters, schoolNames: newSelected });
                        }}
                        className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                        aria-label={`Remove ${schoolName}`}
                      >
                        <span className="text-blue-700 hover:text-red-600 font-bold">×</span>
                      </button>
                    </span>
                  ))}
                </div>
              )}
              
              <div className="relative">
                <input
                  type="text"
                  value={schoolSearchTerm}
                  onChange={(e) => {
                    setSchoolSearchTerm(e.target.value);
                  }}
                  placeholder="Type at least 2 characters to search..."
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    selectedFilters.schoolNames?.length > 0
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300'
                  }`}
                />
                {schoolSearchTerm && schoolSearchTerm.trim().length < 2 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Type at least 2 characters to search schools
                  </p>
                )}
                {schoolSearchTerm && schoolSearchTerm.trim().length >= 2 && schools.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    No schools found matching "{schoolSearchTerm}"
                  </p>
                )}
                {schoolSearchTerm && schoolSearchTerm.trim().length >= 2 && schools.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFilters({ ...selectedFilters, schoolNames: [] });
                        setSchoolSearchTerm('');
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm font-medium border-b"
                    >
                      Clear All Selections
                    </button>
                    {schools.map(school => {
                      const isSelected = selectedFilters.schoolNames?.includes(school.name) || false;
                      return (
                        <button
                          key={school.id}
                          type="button"
                          onClick={() => {
                            const currentSelected = selectedFilters.schoolNames || [];
                            let newSelected;
                            if (isSelected) {
                              // Remove from selection
                              newSelected = currentSelected.filter(name => name !== school.name);
                            } else {
                              // Add to selection
                              newSelected = [...currentSelected, school.name];
                            }
                            setSelectedFilters({ ...selectedFilters, schoolNames: newSelected });
                          }}
                          className={`w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center gap-2 ${
                            isSelected ? 'bg-blue-100 border-l-2 border-blue-500' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}} // Handled by button onClick
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className={isSelected ? 'font-medium text-blue-700 flex-1' : 'flex-1'}>
                            {school.name}
                            {school.city && (
                              <span className="text-xs text-gray-500 ml-2">({school.city})</span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                    {schools.length >= 20 && (
                      <div className="px-4 py-2 text-xs text-gray-500 border-t">
                        Showing first 20 results. Refine your search for more specific results.
                      </div>
                    )}
                  </div>
                )}
              </div>
              {selectedFilters.schoolNames && selectedFilters.schoolNames.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-600">Selected:</span>
                    {selectedFilters.schoolNames.map((schoolName, index) => (
                      <span
                        key={index}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded flex items-center gap-1"
                      >
                        {schoolName}
                        <button
                          type="button"
                          onClick={() => {
                            const newSelected = selectedFilters.schoolNames.filter(name => name !== schoolName);
                            setSelectedFilters({ ...selectedFilters, schoolNames: newSelected });
                          }}
                          className="text-blue-700 hover:text-red-600 ml-1"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFilters({ ...selectedFilters, schoolNames: [] });
                        setSchoolSearchTerm('');
                      }}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              )}
                </div>
              </>
            )}
          </div>

          {/* Data Preview */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">Data Preview</label>
              <span className="text-xs text-gray-600">
                {loading ? 'Loading...' : `Showing first ${Math.min(previewData.length, 3)} rows`}
              </span>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Loading preview data...</p>
                </div>
              ) : previewData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-gray-50">
                      <tr>
                        {getHeaders().map((header, idx) => (
                          <th key={idx} className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-600 uppercase">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewData.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {getHeaders().map((header, cellIdx) => (
                            <td key={cellIdx} className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                              {row[header] || 'N/A'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-sm text-gray-600">No data available for the selected filters</p>
                </div>
              )}
            </div>
          </div>

          {/* Estimated Results */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">Estimated Results</p>
                <p className="text-xs text-blue-700 mt-1">Based on current filters</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-900">
                  {loading ? '...' : recordCount.toLocaleString()}
                </p>
                <p className="text-xs text-blue-700">records</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3">
          <button
            onClick={onClose}
            className="px-4 sm:px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base touch-target order-2 sm:order-1"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const filters = buildFilters();
              const format = category?.id === 'player_cards' ? 'PDF' : selectedFormat;
              
              // Validate school selection for player_cards
              if (category?.id === 'player_cards' && !filters.schoolId) {
                alert('Please select a school to continue.');
                return;
              }
              
              onConfirm(format, filters);
            }}
            disabled={loading || recordCount === 0 || (category?.id === 'player_cards' && !selectedFilters.schoolId)}
            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base touch-target order-1 sm:order-2"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">
              {category?.id === 'player_cards' ? `Export Player Cards` : `Export ${recordCount} Record${recordCount !== 1 ? 's' : ''}`}
            </span>
            <span className="sm:hidden">
              {category?.id === 'player_cards' ? 'Export' : `Export ${recordCount}`}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportPreview;